import { forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useDrag } from 'react-dnd';
import { Source, Annote, Takeaway, Note } from '@annote/types';
import { useDragging } from 'src/context/DraggingContext';
import { colors } from '@annote/core/styles/colors';
import { ListTakeawayCard } from './byType/takeaway/ListTakeaway';
import { ListSourceCard } from './byType/source/ListSource';
import { ListAnnoteCard } from './byType/annote/ListAnnote';
import { CardProps } from './Card';

// Get drag type based on note type for better drop filtering
const getListDragType = (note: Note): string => {
  switch (note.type) {
    case 'takeaway':
      return 'TAKEAWAY';
    case 'source':
      return 'SOURCE';
    case 'annote':
      return 'ANNOTE';
    default:
      return 'LISTCARD';
  }
};
export const CardList = styled.ol`
  list-style: none;
  display: flex;
  flex-direction: column;
  overflow: visible;
  padding: 0;
`;

const ListCardOutline = styled.div<{ isDragging: boolean; disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: ${colors.bg.primary};
  min-height: 2rem;
  user-select: none;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  box-shadow: 0px 1px 2px 0px rgba(15, 15, 15, 0.16), 0px 0px 1px 0px rgba(0, 0, 0, 0.12);
  border: 1px solid #f5f5f5;
`;

export const ListCard = forwardRef<HTMLDivElement, CardProps & { disabled?: boolean }>(
  ({ note, disabled = false }, forwardedRef) => {
    const internalRef = useRef<HTMLDivElement | null>(null);
    const { setDraggingNoteKey, draggingNoteKey } = useDragging();

    // unique key (don't use just id as there could be a non-list card)
    const key = `listcard-${note.id}`;

    const [{ isDragging }, dragRef] = useDrag({
      type: getListDragType(note),
      item: note,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    useEffect(() => {
      if (isDragging) {
        setDraggingNoteKey(key);
      } else if (draggingNoteKey === key) {
        setDraggingNoteKey(null);
      }
    }, [isDragging, setDraggingNoteKey, draggingNoteKey, note.id]);

    useImperativeHandle(forwardedRef, () => internalRef.current as HTMLDivElement);

    dragRef(internalRef);

    return (
      <ListCardOutline ref={internalRef} id={key} isDragging={isDragging} disabled={disabled}>
        {note.type === 'takeaway' && <ListTakeawayCard takeaway={note as Takeaway} />}
        {note.type === 'source' && <ListSourceCard source={note as Source} />}
        {note.type === 'annote' && <ListAnnoteCard annote={note as Annote} />}
      </ListCardOutline>
    );
  },
);

export default ListCard;
